export class ApiError extends Error {
  status?: number;
  code?: string;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/** Même origine : CloudFront (prod) ou proxy Vite (dev) servent /api. */
export const API_BASE = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || '/api';

let refreshing: Promise<boolean> | null = null;
let onSessionLost: () => void = () => {};

/** Appelé quand la session ne peut plus être renouvelée (refresh token expiré ou révoqué). */
export function setSessionLostHandler(fn: () => void) {
  onSessionLost = fn;
}

/** Renouvelle le cookie de session ; un seul appel en vol, partagé. */
export function refreshSession(): Promise<boolean> {
  if (!refreshing) {
    refreshing = fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'same-origin' })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

async function send(path: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(`${API_BASE}${path}`, {
      ...init,
      credentials: 'same-origin',
      headers: {
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiError('Impossible de contacter le serveur. Vérifiez votre connexion.');
  }
}

/** Routes publiques : un 401 y est une réponse métier, pas une session expirée. */
const PUBLIC_AUTH_PATHS = ['/auth/login', '/auth/activate', '/auth/refresh', '/auth/forgot', '/auth/reset', '/auth/logout'];

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let res = await send(path, init);

  // Session expirée (cookie de 15 min) : renouvellement puis rejeu, une fois.
  if (res.status === 401 && !PUBLIC_AUTH_PATHS.includes(path)) {
    if (await refreshSession()) {
      res = await send(path, init);
    }
    if (res.status === 401) {
      onSessionLost();
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(
      body?.error?.message ?? `Erreur serveur (${res.status}).`,
      res.status,
      body?.error?.code
    );
  }

  if (res.status === 204) return undefined as T;

  // En prod, les pages d'erreur SPA de CloudFront réécrivent les 404 de
  // l'API en index.html + 200 : une réponse non-JSON équivaut à un 404.
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new ApiError('Ressource introuvable.', 404, 'NOT_FOUND');
  }

  return res.json() as Promise<T>;
}
